import { useCallback } from 'react';

import { Feature } from 'config/features';
import { emptyDestination } from 'config/SearchForm/defaults';

import { useFeature } from 'hooks/useFeature';

import {
    DestinationValue,
    DirectSearchType,
    SearchStep,
} from 'types/Search/form';

import {
    DestinationEntry,
    DestinationType,
    TargetDistance,
    TopRegion,
} from '../types';

interface UseDestinationValueProps {
    destinationValue: DestinationValue;
    onDestinationValueChange: (value: DestinationValue) => void;
}

export const useDestinationValue = ({
    destinationValue,
    onDestinationValueChange,
}: UseDestinationValueProps) => {
    const isKillS2Enabled = useFeature(Feature.KillS2);

    const updateTopRegion = useCallback(
        (entry: TopRegion, label: string) => {
            onDestinationValueChange({
                ...emptyDestination,
                directSearch: DirectSearchType.Region,
                regionIdList: new Set([entry.regionId]),
                label,
                searchStep: SearchStep.Hotel,
            });
        },
        [onDestinationValueChange]
    );

    const updateFlightDistance = useCallback(
        (entry: TargetDistance, label: string) => {
            onDestinationValueChange({
                ...emptyDestination,
                flightDistance: entry.value,
                directSearch: DirectSearchType.Region,
                searchStep: SearchStep.Region,
                label,
            });
        },
        [onDestinationValueChange]
    );

    const updateListElement = useCallback(
        (destinationEntry: DestinationEntry, label: string) => {
            const entry: DestinationEntry =
                destinationEntry.type === DestinationType.Region &&
                destinationEntry.isRegionGroup
                    ? {
                          ...destinationEntry,
                          type: DestinationType.RegionGroup,
                      }
                    : destinationEntry;
            const isKillS2Destination = isKillS2Enabled && entry.resolve;
            switch (entry.type) {
                case DestinationType.Hotel:
                    onDestinationValueChange({
                        ...emptyDestination,
                        hotelId: entry.id,
                        regionIdList: new Set(entry.regions),
                        directSearch: DirectSearchType.Hotel,
                        searchStep:
                            destinationValue.searchStep === SearchStep.Offer
                                ? SearchStep.Offer
                                : SearchStep.Hotel,
                        label,
                    });
                    break;
                case DestinationType.City:
                    onDestinationValueChange({
                        ...emptyDestination,
                        cityIdList: new Set([entry.id]),
                        regionIdList: new Set(entry.regions),
                        directSearch: DirectSearchType.City,
                        searchStep: SearchStep.Hotel,
                        label,
                    });
                    break;
                case DestinationType.Region:
                    onDestinationValueChange({
                        ...emptyDestination,
                        regionIdList: new Set(entry.regions),
                        directSearch: DirectSearchType.Region,
                        searchStep: SearchStep.Hotel,
                        label,
                    });
                    break;
                case DestinationType.Country:
                    onDestinationValueChange({
                        ...emptyDestination,
                        countryIdList: new Set([entry.id]),
                        directSearch: isKillS2Destination
                            ? DirectSearchType.Country
                            : DirectSearchType.Region,
                        searchStep: isKillS2Destination
                            ? SearchStep.Hotel
                            : SearchStep.Region,
                        label,
                    });
                    break;
                case DestinationType.RegionGroup:
                    onDestinationValueChange({
                        ...emptyDestination,
                        regionGroupIdList: new Set(entry.regions),
                        directSearch: isKillS2Destination
                            ? DirectSearchType.RegionGroup
                            : DirectSearchType.Region,
                        searchStep: isKillS2Destination
                            ? SearchStep.Hotel
                            : SearchStep.Region,
                        label,
                    });
                    break;
                case DestinationType.CtsDestination:
                    onDestinationValueChange({
                        ...emptyDestination,
                        ctsDestination: entry.id,
                        directSearch: DirectSearchType.CtsDestination,
                        searchStep: SearchStep.Hotel,
                        label,
                    });
                    break;
            }
        },
        [destinationValue.searchStep, isKillS2Enabled, onDestinationValueChange]
    );

    const resetDestination = useCallback(() => {
        onDestinationValueChange(emptyDestination);
    }, [onDestinationValueChange]);

    return {
        updateTopRegion,
        updateFlightDistance,
        resetDestination,
        updateListElement,
    };
};
